/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useEffect, useRef } from 'react';
import { useContentStackBlogData } from '../api/getContentStackBlogData';
import { useContentStackRelatedBlogsData } from '../api/getContentStackRelatedBlogsData';
import styles from './Blog.module.scss';
import { useRouter } from 'next/navigation';
import BlogMenuNavigation from './BlogMenuNavigation';
import { BlogHeading } from './BlogHeading/BlogHeading';
import { SharingTooltip } from './SharingTooltip';
import { CMSModularHandler } from './CMSModularHandler';
import { View } from '@az/starc-ui';
import { TableOfContents } from './TableOfContents/TableOfContents';
import cx from 'classnames';
import { ImageVideoContent } from './BlogImageVideoContent/ImageVideoContent';
import BlogBreadcrumb from './BlogsBreadcrumb';
import { CMSBlogSeoTags } from './CMSBlogSeoTags/CMSBlogSeoTags';
import { BlogProductTypeGrid } from './BlogProductGrid/BlogProductTypeGrid';
import { RelatedBlogs } from './CMSRelatedBlogs/RelatedBlogs';
import { CMSSpacer } from '@/features/contentstack/components/CMSSpacer/CMSSpacer';
import { useLocale } from '@/hooks/useLocale';
import { useBlogAnalytics } from './useBlogAnalytics';
import { BlogReviewer } from './BlogReviewer/BlogReviewer';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useGetCertonaRecommendations } from '@/features/certona/api/useGetCertonaRecommendations';
import { certonaPageType } from '@/constants/certonaPageType';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useHeaderData } from '@/features/header/api/getHeader';
import { CMSCertona } from '@/features/contentstack/components/CMSCertona/CMSCertona';
import { type ContentStackCertona } from '@/features/contentstack/interface';
import { blogPrefix } from '../utils/isValidBlogLocale';
import { useContentStackLivePreview } from '@/hooks/useContentStackLivePreview';

const Blog = () => {
  const { data: blogResponse, isFetching: isFetchingBlogData } = useContentStackBlogData();
  const router = useRouter();
  const locale = useLocale();
  const { data: headerData } = useHeaderData();
  const preferredVehicle = usePreferredVehicle();
  const vehicleId = (
    headerData?.vehicleMap?.catalogVehicleId ||
    preferredVehicle?.partsProVehicleId ||
    ''
  ).toString();
  const certonaRecommendations = useGetCertonaRecommendations(
    {
      certonaType: certonaPageType.blog,
      vehicleID: vehicleId,
      recommendations: true,
    },
    [vehicleId],
    true,
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const { blogContent: blogData } = blogResponse ?? {};
  const isStaticPostType = blogData?.post_type === 'Static';
  const shouldShowToc = !isStaticPostType && blogData?.post_attributes?.show_toc !== false;

  const { data: relatedBlogContent, isFetching: isFetchingRelatedBlogs } =
    useContentStackRelatedBlogsData({
      primaryCategory: blogData?.post_content_category?.primary_category ?? '',
      currentBlogUrl: blogData?.url ?? '',
      postType: blogData?.post_type ?? '',
    });
  const reviewer = blogData?.reviewer?.[0];
  const reviewed_date = blogData?.reviewed_date;

  const isLoading = isFetchingBlogData || isFetchingRelatedBlogs;

  useBlogAnalytics({ blogData, locale });

  useContentStackLivePreview({ enabled: true });

  useEffect(() => {
    if (!isFetchingBlogData && !blogData) {
      const blogRoot = blogPrefix[locale];

      void router.replace(blogRoot);
    }
  }, [blogData, locale, router, isFetchingBlogData]);

  const certonaContent: ContentStackCertona | null =
    Array.isArray(blogData?.certona) && blogData.certona.length > 0
      ? {
          ...blogData.certona[0],
          title: `Blog ${blogData.certona[0].container_id}`,
        }
      : null;

  return (
    <>
      {blogData?.url && blogData?.seo ? (
        <CMSBlogSeoTags currentURL={blogData.url} seoData={blogData.seo} blogData={blogData} />
      ) : null}
      {isLoading ? <ProgressModal noScroll /> : null}
      <View>
        <View padding={{ s: [0, 4] }}>
          <BlogMenuNavigation />
          {blogData?.post_content_category?.primary_category && (
            <BlogBreadcrumb primaryCategory={blogData?.post_content_category?.primary_category} />
          )}
          <View
            direction={{ s: 'column-reverse', m: 'row' }}
            justify={{ s: 'start', m: 'space-between' }}
            className={cx(styles.blogHeaderContainer, { [styles.staticBlog]: isStaticPostType })}
          >
            <BlogHeading blogHeading={blogData?.post_heading} isStaticPostType={isStaticPostType} />
            {!isStaticPostType && (
              <View direction={{ s: 'row', m: 'column' }} justify={{ s: 'end' }}>
                <View padding={3}>
                  <SharingTooltip />
                </View>
              </View>
            )}
          </View>
          {!isStaticPostType && reviewer?.post_review && (
            <BlogReviewer blogReviewer={reviewer} date={reviewed_date} />
          )}
          <ImageVideoContent
            content={blogData?.feature_content}
            preload={'auto'}
            mediaType={blogData?.feature_content?.media_type}
            blogCreatedDate={blogData?.created_at}
            priority
          />
          <BlogProductTypeGrid content={blogData?.product_grid} gridIndex={0} />
        </View>
        <View
          direction={{ s: 'column', l: 'row' }}
          justify="space-between"
          className={cx(styles.blogContentContainer, {
            [styles.blogContentContainerFullWidth]: !isStaticPostType && !shouldShowToc,
          })}
        >
          {shouldShowToc && !isFetchingBlogData && <TableOfContents contentRef={contentRef} />}
          <View
            className={cx({
              [styles.staticBlogContent]: isStaticPostType,
              [styles.blogContent]: !isStaticPostType && shouldShowToc,
              [styles.blogContentFullWidth]: !isStaticPostType && !shouldShowToc,
            })}
            attributes={{ ref: contentRef }}
            direction="column"
          >
            <CMSModularHandler
              content={blogData?.post_content}
              certonaRecommendations={certonaRecommendations}
              blogCreatedDate={blogData?.created_at}
            />
            <View>
              <div
                className={styles.qualtricsContainer}
                id="qualtrics-embedded-feedback"
                aria-label="Feedback form"
                role="region"
              />
            </View>
          </View>
        </View>
        {certonaContent && (
          <CMSSpacer>
            <CMSCertona content={certonaContent} certonaRecommendations={certonaRecommendations} />
          </CMSSpacer>
        )}
        {blogData?.post_content_category?.primary_category && (
          <CMSSpacer>
            <RelatedBlogs relatedBlogContent={relatedBlogContent || []} />
          </CMSSpacer>
        )}
      </View>
    </>
  );
};

export default Blog;
